import { getFirestoreUtils, getFunctionsInstance } from '@/lib/firebase'
import { httpsCallable } from 'firebase/functions'
import { useAuthStore } from '@/stores/authStore'
import type {
  SharedGroup, SharedTask, MemberRole,
} from '@/lib/sharedTypes'

// ─── 헬퍼 ───

function generateInviteCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
  let code = ''
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return code
}

function getUserInfo() {
  const user = useAuthStore.getState().user
  if (!user) throw new Error('로그인 필요')
  return { uid: user.uid, displayName: user.displayName, photoURL: user.photoURL, email: user.email }
}

// ─── 그룹 CRUD ───

export async function createSharedGroup(
  name: string, color: string, description?: string, icon?: string
): Promise<string> {
  const { firestore, collection, doc, setDoc } = await getFirestoreUtils()
  const user = getUserInfo()
  const now = new Date().toISOString()

  const groupRef = doc(collection(firestore, 'sharedGroups'))
  const groupId = groupRef.id

  const group: Omit<SharedGroup, 'id'> = {
    name,
    color,
    description: description || '',
    icon: icon || '',
    ownerId: user.uid,
    ownerName: user.displayName,
    ownerPhotoURL: user.photoURL || '',
    memberIds: [user.uid],
    memberCount: 1,
    maxMembers: 10,
    inviteCode: generateInviteCode(),
    createdAt: now,
    updatedAt: now,
    lastActivityAt: now,
  }

  await setDoc(groupRef, group)

  // 멤버 서브컬렉션에 owner 추가
  await setDoc(doc(firestore, `sharedGroups/${groupId}/members/${user.uid}`), {
    uid: user.uid,
    displayName: user.displayName,
    email: user.email,
    photoURL: user.photoURL || '',
    role: 'owner' as MemberRole,
    joinedAt: now,
    lastSeenAt: now,
    status: 'active',
  })

  return groupId
}

export async function updateSharedGroup(
  groupId: string,
  updates: Partial<Pick<SharedGroup, 'name' | 'color' | 'description' | 'icon' | 'inviteCode' | 'thumbnailUrl' | 'announcement' | 'announcementSetBy' | 'announcementSetByName' | 'announcementSetAt'>>
): Promise<void> {
  const { firestore, doc, updateDoc } = await getFirestoreUtils()
  // undefined 값 필터링 — Firestore updateDoc은 undefined를 허용하지 않음
  const updateData: Record<string, unknown> = { updatedAt: new Date().toISOString() }
  for (const [key, value] of Object.entries(updates)) {
    if (value !== undefined) {
      updateData[key] = value
    }
  }
  await updateDoc(doc(firestore, `sharedGroups/${groupId}`), updateData)
}

// ─── 공유 할 일 CRUD ───

export async function addSharedTask(
  groupId: string,
  task: Pick<SharedTask, 'title' | 'description' | 'priority' | 'dueDate' | 'dueTime'> & {
    assigneeIds?: string[]
    assigneeNames?: string[]
    subtasks?: { title: string }[]
    attachments?: File[]
  }
): Promise<string> {
  const { firestore, collection, doc, setDoc, getDocs } = await getFirestoreUtils()
  const user = getUserInfo()
  const now = new Date().toISOString()

  // sortOrder 계산
  const existingTasks = await getDocs(collection(firestore, `sharedGroups/${groupId}/tasks`))
  const maxSort = existingTasks.docs.reduce(
    (max, d) => Math.max(max, (d.data() as { sortOrder?: number }).sortOrder || 0), 0
  )

  const taskRef = doc(collection(firestore, `sharedGroups/${groupId}/tasks`))
  const taskId = taskRef.id

  const subtaskCount = task.subtasks?.length || 0

  // undefined 값을 제외한 태스크 데이터 구성 (Firestore는 undefined 허용하지 않음)
  const newTask: Record<string, unknown> = {
    title: task.title,
    description: task.description || '',
    status: 'pending',
    priority: task.priority || 'none',
    createdBy: user.uid,
    createdByName: user.displayName,
    subtaskCount,
    completedSubtaskCount: 0,
    sortOrder: maxSort + 1,
    createdAt: now,
    updatedAt: now,
  }
  if (task.dueDate) newTask.dueDate = task.dueDate
  if (task.dueTime) newTask.dueTime = task.dueTime
  // 복수 담당자
  if (task.assigneeIds && task.assigneeIds.length > 0) {
    newTask.assigneeIds = task.assigneeIds
    newTask.assigneeNames = task.assigneeNames || []
    // 하위 호환: 첫번째 담당자를 단일 필드에도 기록
    newTask.assigneeId = task.assigneeIds[0]
    newTask.assigneeName = task.assigneeNames?.[0] || ''
  }

  await setDoc(taskRef, newTask)

  // 하위 작업 생성
  if (task.subtasks && task.subtasks.length > 0) {
    const subtaskPromises = task.subtasks.map((st, i) => {
      const subtaskRef = doc(collection(firestore, `sharedGroups/${groupId}/tasks/${taskId}/subtasks`))
      return setDoc(subtaskRef, {
        title: st.title,
        isCompleted: false,
        sortOrder: i + 1,
        createdAt: now,
      })
    })
    await Promise.all(subtaskPromises)
  }

  // 첨부파일 업로드 (Firebase Storage) — 이미지는 자동 압축
  if (task.attachments && task.attachments.length > 0) {
    const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage')
    const { compressImage } = await import('@/lib/imageCompress')
    const storage = getStorage()

    const attachmentMetas: { name: string; url: string; size: number; type: string }[] = []
    for (const file of task.attachments) {
      const processed = await compressImage(file)
      const filePath = `sharedGroups/${groupId}/tasks/${taskId}/${Date.now()}_${file.name}`
      const storageRef = ref(storage, filePath)
      await uploadBytes(storageRef, processed)
      const url = await getDownloadURL(storageRef)
      attachmentMetas.push({ name: file.name, url, size: processed.size, type: processed.type })
    }

    const { updateDoc } = await import('firebase/firestore')
    await updateDoc(taskRef, { attachments: attachmentMetas })
  }

  // 활동 로그
  await addActivity(groupId, 'task_created', taskId, task.title)

  return taskId
}

export async function updateSharedTask(
  groupId: string,
  taskId: string,
  updates: Partial<SharedTask>
): Promise<void> {
  const { firestore, doc, updateDoc } = await getFirestoreUtils()

  // undefined 값 필터링
  const updateData: Record<string, unknown> = { updatedAt: new Date().toISOString() }
  for (const [key, value] of Object.entries(updates)) {
    if (value !== undefined) {
      updateData[key] = value
    }
  }
  await updateDoc(doc(firestore, `sharedGroups/${groupId}/tasks/${taskId}`), updateData)
}

export async function toggleSharedTaskComplete(
  groupId: string,
  taskId: string,
  currentStatus: 'pending' | 'completed'
): Promise<void> {
  const { firestore, doc, updateDoc } = await getFirestoreUtils()
  const { deleteField } = await import('firebase/firestore')
  const user = getUserInfo()
  const now = new Date().toISOString()

  if (currentStatus === 'pending') {
    await updateDoc(doc(firestore, `sharedGroups/${groupId}/tasks/${taskId}`), {
      status: 'completed',
      completedBy: user.uid,
      completedByName: user.displayName,
      completedAt: now,
      updatedAt: now,
    })
    await addActivity(groupId, 'task_completed', taskId)
  } else {
    await updateDoc(doc(firestore, `sharedGroups/${groupId}/tasks/${taskId}`), {
      status: 'pending',
      completedBy: deleteField(),
      completedByName: deleteField(),
      completedAt: deleteField(),
      updatedAt: now,
    })
    await addActivity(groupId, 'task_uncompleted', taskId)
  }
}

export async function deleteSharedTask(groupId: string, taskId: string): Promise<void> {
  const { firestore, doc, deleteDoc, collection, getDocs } = await getFirestoreUtils()

  // 서브컬렉션 삭제 (Firestore는 자동 cascade 삭제하지 않음)
  const [subtasksSnap, commentsSnap] = await Promise.all([
    getDocs(collection(firestore, `sharedGroups/${groupId}/tasks/${taskId}/subtasks`)),
    getDocs(collection(firestore, `sharedGroups/${groupId}/tasks/${taskId}/comments`)),
  ])
  const subDeletes = [
    ...subtasksSnap.docs.map((d) => deleteDoc(d.ref)),
    ...commentsSnap.docs.map((d) => deleteDoc(d.ref)),
  ]
  if (subDeletes.length > 0) await Promise.all(subDeletes)

  await deleteDoc(doc(firestore, `sharedGroups/${groupId}/tasks/${taskId}`))
  await addActivity(groupId, 'task_deleted', taskId)
}

export async function assignSharedTask(
  groupId: string, taskId: string,
  assigneeIds: string[] | null, assigneeNames: string[] | null
): Promise<void> {
  await updateSharedTask(groupId, taskId, {
    assigneeIds: assigneeIds || [],
    assigneeNames: assigneeNames || [],
    // 하위 호환: 첫번째 담당자
    assigneeId: assigneeIds?.[0] || undefined,
    assigneeName: assigneeNames?.[0] || undefined,
  })
  if (assigneeIds && assigneeIds.length > 0) {
    await addActivity(groupId, 'task_assigned', taskId, assigneeNames?.join(', ') || undefined)
  }
}

// ─── 초대 ───

export async function sendInvitation(
  groupId: string, groupName: string, groupColor: string,
  inviteeEmail: string, role: 'editor' | 'viewer'
): Promise<string> {
  const { firestore, collection, doc, setDoc } = await getFirestoreUtils()
  const user = getUserInfo()
  const now = new Date()
  const expires = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000) // 7일

  const inviteRef = doc(collection(firestore, 'invitations'))

  await setDoc(inviteRef, {
    groupId,
    groupName,
    groupColor,
    inviterId: user.uid,
    inviterName: user.displayName,
    inviterPhotoURL: user.photoURL || '',
    inviteeEmail: inviteeEmail.toLowerCase().trim(),
    role,
    status: 'pending',
    createdAt: now.toISOString(),
    expiresAt: expires.toISOString(),
  })

  return inviteRef.id
}

// ─── Cloud Function 호출 ───

export async function callAcceptInvitation(invitationId: string) {
  const functions = await getFunctionsInstance()
  const fn = httpsCallable(functions, 'acceptInvitation')
  return fn({ invitationId })
}

export async function callDeclineInvitation(invitationId: string) {
  const functions = await getFunctionsInstance()
  const fn = httpsCallable(functions, 'declineInvitation')
  return fn({ invitationId })
}

export async function callLeaveGroup(groupId: string) {
  const functions = await getFunctionsInstance()
  const fn = httpsCallable(functions, 'leaveGroup')
  return fn({ groupId })
}

export async function callRemoveMember(groupId: string, targetUid: string) {
  const functions = await getFunctionsInstance()
  const fn = httpsCallable(functions, 'removeMember')
  return fn({ groupId, targetUid })
}

export async function callDeleteSharedGroup(groupId: string) {
  const functions = await getFunctionsInstance()
  const fn = httpsCallable(functions, 'deleteSharedGroup')
  return fn({ groupId })
}

export async function callTransferOwnership(groupId: string, newOwnerId: string) {
  const functions = await getFunctionsInstance()
  const fn = httpsCallable(functions, 'transferOwnership')
  return fn({ groupId, newOwnerId })
}

export async function callChangeMemberRole(
  groupId: string, targetUid: string, newRole: 'editor' | 'editor-limited' | 'viewer'
) {
  const functions = await getFunctionsInstance()
  const fn = httpsCallable(functions, 'changeMemberRole')
  return fn({ groupId, targetUid, newRole })
}

// ─── 초대 코드로 그룹 참여 (Cloud Function 경유) ───

export async function joinGroupByInviteCode(inviteCode: string): Promise<{ groupId: string; groupName: string }> {
  const { httpsCallable: callFunction } = await import('firebase/functions')
  const { getFunctionsInstance: getFunctions } = await import('@/lib/firebase')
  const functions = await getFunctions()

  const fn = callFunction<{ inviteCode: string }, { groupId: string; groupName: string; alreadyMember: boolean }>(
    functions, 'joinGroupByCode'
  )
  const result = await fn({ inviteCode: inviteCode.trim() })
  return { groupId: result.data.groupId, groupName: result.data.groupName }
}

// ─── 리액션 ───

export async function addReaction(groupId: string, taskId: string, emoji: string): Promise<void> {
  const { firestore, doc } = await getFirestoreUtils()
  const { runTransaction } = await import('firebase/firestore')
  const user = getUserInfo()

  const taskRef = doc(firestore, `sharedGroups/${groupId}/tasks/${taskId}`)
  await runTransaction(firestore, async (transaction) => {
    const snap = await transaction.get(taskRef)
    if (!snap.exists()) return

    const reactions = snap.data().reactions || {}
    const current = reactions[emoji] || []
    if (!current.includes(user.uid)) {
      reactions[emoji] = [...current, user.uid]
    }
    transaction.update(taskRef, { reactions, updatedAt: new Date().toISOString() })
  })
}

export async function removeReaction(groupId: string, taskId: string, emoji: string): Promise<void> {
  const { firestore, doc } = await getFirestoreUtils()
  const { runTransaction } = await import('firebase/firestore')
  const user = getUserInfo()

  const taskRef = doc(firestore, `sharedGroups/${groupId}/tasks/${taskId}`)
  await runTransaction(firestore, async (transaction) => {
    const snap = await transaction.get(taskRef)
    if (!snap.exists()) return

    const reactions = snap.data().reactions || {}
    const current = reactions[emoji] || []
    reactions[emoji] = current.filter((uid: string) => uid !== user.uid)
    if (reactions[emoji].length === 0) delete reactions[emoji]
    transaction.update(taskRef, { reactions, updatedAt: new Date().toISOString() })
  })
}

// ─── 댓글 ───

export async function addComment(
  groupId: string,
  taskId: string,
  text: string,
  mentions?: string[],
  parentId?: string,
  image?: { url: string; width: number; height: number },
): Promise<string> {
  const { firestore, collection, doc } = await getFirestoreUtils()
  const { increment, writeBatch } = await import('firebase/firestore')
  const user = getUserInfo()
  const now = new Date().toISOString()

  const commentRef = doc(collection(firestore, `sharedGroups/${groupId}/tasks/${taskId}/comments`))
  const batch = writeBatch(firestore)

  batch.set(commentRef, {
    userId: user.uid,
    userName: user.displayName,
    userPhotoURL: user.photoURL || '',
    text,
    mentions: mentions || [],
    ...(parentId ? { parentId } : {}),
    ...(image ? { imageUrl: image.url, imageWidth: image.width, imageHeight: image.height } : {}),
    createdAt: now,
  })
  batch.set(doc(firestore, `sharedGroups/${groupId}/tasks/${taskId}`), {
    commentCount: increment(1),
    updatedAt: now,
  }, { merge: true })

  await batch.commit()
  await addActivity(groupId, 'task_commented', taskId)
  if (mentions && mentions.length > 0) {
    await addActivity(groupId, 'member_mentioned', taskId)
  }
  return commentRef.id
}

export async function uploadCommentImage(
  groupId: string,
  taskId: string,
  file: File,
): Promise<{ url: string; width: number; height: number }> {
  let uploadFile: File | Blob = file
  try {
    const { compressImage } = await import('@/lib/imageCompress')
    uploadFile = await compressImage(file)
  } catch { /* use original */ }

  const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage')
  const storage = getStorage()
  const ext = file.name.split('.').pop() || 'jpg'
  const fileName = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`
  const storageRef = ref(storage, `commentImages/${groupId}/${taskId}/${fileName}`)

  await uploadBytes(storageRef, uploadFile)
  const url = await getDownloadURL(storageRef)

  let width = 300, height = 200
  try {
    const dims = await new Promise<{ w: number; h: number }>((resolve) => {
      const img = new Image()
      const blobUrl = URL.createObjectURL(file)
      img.onload = () => { URL.revokeObjectURL(blobUrl); resolve({ w: img.width, h: img.height }) }
      img.onerror = () => { URL.revokeObjectURL(blobUrl); resolve({ w: 300, h: 200 }) }
      img.src = blobUrl
    })
    width = dims.w
    height = dims.h
  } catch { /* defaults */ }

  return { url, width, height }
}

export async function deleteComment(groupId: string, taskId: string, commentId: string): Promise<void> {
  const { firestore, doc } = await getFirestoreUtils()
  const { increment, writeBatch } = await import('firebase/firestore')

  const batch = writeBatch(firestore)
  batch.delete(doc(firestore, `sharedGroups/${groupId}/tasks/${taskId}/comments/${commentId}`))
  batch.set(doc(firestore, `sharedGroups/${groupId}/tasks/${taskId}`), {
    commentCount: increment(-1),
    updatedAt: new Date().toISOString(),
  }, { merge: true })
  await batch.commit()
}

// ─── 서브태스크 ───

export async function addSubtask(groupId: string, taskId: string, title: string): Promise<string> {
  const { firestore, collection, doc, getDocs } = await getFirestoreUtils()
  const { increment, writeBatch } = await import('firebase/firestore')
  const now = new Date().toISOString()

  const existing = await getDocs(collection(firestore, `sharedGroups/${groupId}/tasks/${taskId}/subtasks`))
  const maxSort = existing.docs.reduce((max, d) => Math.max(max, (d.data() as { sortOrder?: number }).sortOrder || 0), 0)

  const subtaskRef = doc(collection(firestore, `sharedGroups/${groupId}/tasks/${taskId}/subtasks`))
  const batch = writeBatch(firestore)

  batch.set(subtaskRef, {
    title,
    isCompleted: false,
    sortOrder: maxSort + 1,
    createdAt: now,
  })
  batch.set(doc(firestore, `sharedGroups/${groupId}/tasks/${taskId}`), {
    subtaskCount: increment(1),
    updatedAt: now,
  }, { merge: true })

  await batch.commit()
  return subtaskRef.id
}

export async function toggleSubtask(groupId: string, taskId: string, subtaskId: string, currentCompleted: boolean): Promise<void> {
  const { firestore, doc } = await getFirestoreUtils()
  const { increment, writeBatch } = await import('firebase/firestore')
  const user = getUserInfo()
  const now = new Date().toISOString()

  const batch = writeBatch(firestore)
  batch.set(doc(firestore, `sharedGroups/${groupId}/tasks/${taskId}/subtasks/${subtaskId}`), {
    isCompleted: !currentCompleted,
    completedBy: !currentCompleted ? user.uid : null,
    completedByName: !currentCompleted ? user.displayName : null,
  }, { merge: true })
  batch.set(doc(firestore, `sharedGroups/${groupId}/tasks/${taskId}`), {
    completedSubtaskCount: increment(!currentCompleted ? 1 : -1),
    updatedAt: now,
  }, { merge: true })

  await batch.commit()
}

export async function deleteSubtask(groupId: string, taskId: string, subtaskId: string, wasCompleted: boolean): Promise<void> {
  const { firestore, doc } = await getFirestoreUtils()
  const { increment, writeBatch } = await import('firebase/firestore')

  const batch = writeBatch(firestore)
  batch.delete(doc(firestore, `sharedGroups/${groupId}/tasks/${taskId}/subtasks/${subtaskId}`))

  const updates: Record<string, unknown> = {
    subtaskCount: increment(-1),
    updatedAt: new Date().toISOString(),
  }
  if (wasCompleted) updates.completedSubtaskCount = increment(-1)

  batch.set(doc(firestore, `sharedGroups/${groupId}/tasks/${taskId}`), updates, { merge: true })
  await batch.commit()
}

// ─── 라벨 ───

export async function createLabel(groupId: string, name: string, color: string): Promise<string> {
  const { firestore, collection, doc, setDoc } = await getFirestoreUtils()
  const user = getUserInfo()

  const labelRef = doc(collection(firestore, `sharedGroups/${groupId}/labels`))
  await setDoc(labelRef, {
    name,
    color,
    createdBy: user.uid,
    createdAt: new Date().toISOString(),
  })
  return labelRef.id
}

export async function deleteLabel(groupId: string, labelId: string): Promise<void> {
  const { firestore, doc, deleteDoc } = await getFirestoreUtils()
  await deleteDoc(doc(firestore, `sharedGroups/${groupId}/labels/${labelId}`))
}

export async function addLabelToTask(groupId: string, taskId: string, labelId: string): Promise<void> {
  const { firestore, doc, setDoc } = await getFirestoreUtils()
  const { arrayUnion } = await import('firebase/firestore')
  await setDoc(doc(firestore, `sharedGroups/${groupId}/tasks/${taskId}`), {
    labels: arrayUnion(labelId),
    updatedAt: new Date().toISOString(),
  }, { merge: true })
}

export async function removeLabelFromTask(groupId: string, taskId: string, labelId: string): Promise<void> {
  const { firestore, doc, setDoc } = await getFirestoreUtils()
  const { arrayRemove } = await import('firebase/firestore')
  await setDoc(doc(firestore, `sharedGroups/${groupId}/tasks/${taskId}`), {
    labels: arrayRemove(labelId),
    updatedAt: new Date().toISOString(),
  }, { merge: true })
}

// ─── 채팅: 메시지/리액션/시스템메시지/읽음위치는 RTDB로 이관 (chatRtdbService.ts) ───
// 아래 업로드 함수만 Firebase Storage 사용이므로 유지

// ─── 채팅 이미지 업로드 ───

export async function uploadChatImage(groupId: string, file: File): Promise<{ url: string; width: number; height: number; name: string }> {
  console.info('[uploadChatImage] 시작:', file.name, file.size, file.type)

  // 1) 압축 (실패 시 원본 사용)
  let uploadFile: File | Blob = file
  try {
    const { compressImage } = await import('@/lib/imageCompress')
    uploadFile = await compressImage(file)
    console.info('[uploadChatImage] 압축:', uploadFile.size)
  } catch (err) {
    console.warn('[uploadChatImage] 압축 스킵:', err)
  }

  // 2) Storage 업로드 — 파일 업로드(uploadGroupFile)와 동일한 패턴
  const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage')
  const storage = getStorage()

  const ext = file.name.split('.').pop() || 'jpg'
  const fileName = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`
  const storagePath = `chatImages/${groupId}/${fileName}`
  const storageRef = ref(storage, storagePath)

  await uploadBytes(storageRef, uploadFile)
  const url = await getDownloadURL(storageRef)

  // 3) 이미지 크기 (실패해도 기본값)
  let width = 300, height = 200
  try {
    const dims = await new Promise<{ w: number; h: number }>((resolve) => {
      const img = new Image()
      const blobUrl = URL.createObjectURL(file)
      img.onload = () => { URL.revokeObjectURL(blobUrl); resolve({ w: img.width, h: img.height }) }
      img.onerror = () => { URL.revokeObjectURL(blobUrl); resolve({ w: 300, h: 200 }) }
      img.src = blobUrl
    })
    width = dims.w
    height = dims.h
  } catch { /* 기본값 사용 */ }

  // 4) 그룹 썸네일 자동 설정 (실패 무시)
  try {
    const { firestore, doc } = await getFirestoreUtils()
    const { getDoc, updateDoc } = await import('firebase/firestore')
    const groupDoc = await getDoc(doc(firestore, `sharedGroups/${groupId}`))
    if (groupDoc.exists() && !groupDoc.data().thumbnailUrl) {
      const { generateThumbnailDataUrl } = await import('@/lib/imageCompress')
      const thumbDataUrl = await generateThumbnailDataUrl(file)
      if (thumbDataUrl) {
        await updateDoc(doc(firestore, `sharedGroups/${groupId}`), { thumbnailUrl: thumbDataUrl })
      }
    }
  } catch { /* 썸네일 설정 실패는 무시 */ }

  return { url, width, height, name: file.name }
}

// ─── 음성 메시지 업로드 ───

export async function uploadChatVoice(groupId: string, blob: Blob): Promise<{ url: string; duration: number }> {
  const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage')
  const { app } = await import('@/lib/firebase')
  const storage = getStorage(app)

  const fileName = `${Date.now()}_voice.webm`
  const storageRef = ref(storage, `chatVoice/${groupId}/${fileName}`)
  await uploadBytes(storageRef, blob)
  const url = await getDownloadURL(storageRef)

  // 오디오 길이 계산 (타임아웃 + objectURL 정리)
  const duration = await new Promise<number>((resolve) => {
    const audio = new Audio()
    const blobUrl = URL.createObjectURL(blob)
    const cleanup = () => URL.revokeObjectURL(blobUrl)
    const timeoutId = setTimeout(() => { cleanup(); resolve(0) }, 5000)

    audio.onloadedmetadata = () => {
      clearTimeout(timeoutId)
      const dur = Math.ceil(audio.duration)
      cleanup()
      resolve(isNaN(dur) ? 0 : dur)
    }
    audio.onerror = () => { clearTimeout(timeoutId); cleanup(); resolve(0) }
    audio.src = blobUrl
  })

  return { url, duration }
}

// ─── 파일 업로드 ───

export async function uploadChatFile(groupId: string, file: File): Promise<{ url: string; name: string; size: number; type: string }> {
  const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage')
  const { app } = await import('@/lib/firebase')
  const storage = getStorage(app)

  const fileName = `${Date.now()}_${file.name}`
  const storageRef = ref(storage, `chatFiles/${groupId}/${fileName}`)
  await uploadBytes(storageRef, file)
  const url = await getDownloadURL(storageRef)

  return { url, name: file.name, size: file.size, type: file.type }
}

// 채팅 리액션/시스템 메시지/읽음 위치 → chatRtdbService.ts로 이관 완료

// ─── 핀 ───

export async function pinTask(groupId: string, taskId: string): Promise<void> {
  const { firestore, doc, setDoc } = await getFirestoreUtils()
  const user = getUserInfo()
  await setDoc(doc(firestore, `sharedGroups/${groupId}/tasks/${taskId}`), {
    isPinned: true,
    pinnedAt: new Date().toISOString(),
    pinnedBy: user.uid,
    updatedAt: new Date().toISOString(),
  }, { merge: true })
  await addActivity(groupId, 'task_pinned', taskId)
}

export async function unpinTask(groupId: string, taskId: string): Promise<void> {
  const { firestore, doc } = await getFirestoreUtils()
  const { updateDoc, deleteField } = await import('firebase/firestore')
  await updateDoc(doc(firestore, `sharedGroups/${groupId}/tasks/${taskId}`), {
    isPinned: false,
    pinnedAt: deleteField(),
    pinnedBy: deleteField(),
    updatedAt: new Date().toISOString(),
  })
}

// ─── 온라인 상태 ───

export async function updatePresence(groupId: string, isOnline: boolean): Promise<void> {
  const user = useAuthStore.getState().user
  if (!user) return // 로그인 안 됐으면 무시
  try {
    const { firestore, doc, setDoc } = await getFirestoreUtils()
    await setDoc(doc(firestore, `sharedGroups/${groupId}/members/${user.uid}`), {
      isOnline,
      lastActiveAt: new Date().toISOString(),
    }, { merge: true })
  } catch {
    // 프레즌스 업데이트 실패는 무시
  }
}

// ─── 멘션 파싱 ───

export function parseMentions(text: string, members: { uid: string; displayName: string }[]): { mentionedUids: string[] } {
  const mentionedUids: string[] = []
  for (const member of members) {
    if (text.includes(`@${member.displayName}`)) {
      mentionedUids.push(member.uid)
    }
  }
  return { mentionedUids }
}

// ─── 그룹 파일 관리 ───

export async function uploadGroupFile(
  groupId: string,
  file: File
): Promise<string> {
  const { firestore, collection, doc, setDoc } = await getFirestoreUtils()
  const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage')
  const user = getUserInfo()
  const storage = getStorage()
  const now = new Date().toISOString()

  let processed: File = file
  if (file.type.startsWith('image/')) {
    const { compressImage } = await import('@/lib/imageCompress')
    processed = await compressImage(file)
  }
  const filePath = `sharedGroups/${groupId}/files/${Date.now()}_${file.name}`
  const storageRef = ref(storage, filePath)
  await uploadBytes(storageRef, processed)
  const url = await getDownloadURL(storageRef)

  const fileDocRef = doc(collection(firestore, `sharedGroups/${groupId}/files`))
  await setDoc(fileDocRef, {
    name: file.name,
    url,
    size: processed.size,
    type: processed.type,
    uploadedBy: user.uid,
    uploadedByName: user.displayName,
    createdAt: now,
  })

  await addActivity(groupId, 'file_uploaded', fileDocRef.id, file.name)

  // 이미지 파일이고 그룹 썸네일이 없으면 대표 이미지(Data URL)로 설정
  if (file.type.startsWith('image/')) {
    try {
      const { getDoc, updateDoc } = await import('firebase/firestore')
      const groupDoc = await getDoc(doc(firestore, `sharedGroups/${groupId}`))
      if (groupDoc.exists() && !groupDoc.data().thumbnailUrl) {
        const { generateThumbnailDataUrl } = await import('@/lib/imageCompress')
        const thumbDataUrl = await generateThumbnailDataUrl(processed)
        if (thumbDataUrl) {
          await updateDoc(doc(firestore, `sharedGroups/${groupId}`), { thumbnailUrl: thumbDataUrl })
        }
      }
    } catch { /* 썸네일 설정 실패는 무시 */ }
  }

  return fileDocRef.id
}

export async function deleteGroupFile(
  groupId: string,
  fileId: string,
  fileUrl: string,
  fileName: string
): Promise<void> {
  const { firestore, doc, deleteDoc } = await getFirestoreUtils()

  // Storage에서 파일 삭제
  try {
    const { getStorage, ref, deleteObject } = await import('firebase/storage')
    const storage = getStorage()
    const storageRef = ref(storage, fileUrl)
    await deleteObject(storageRef)
  } catch {
    // Storage 삭제 실패해도 Firestore 문서는 삭제
    console.warn('Storage 파일 삭제 실패 (무시됨)')
  }

  await deleteDoc(doc(firestore, `sharedGroups/${groupId}/files/${fileId}`))
  await addActivity(groupId, 'file_deleted', fileId, fileName)
}

// ─── 활동 로그 헬퍼 ───

async function addActivity(
  groupId: string,
  action: string,
  targetId?: string,
  targetTitle?: string
) {
  try {
    const { firestore, collection, doc, setDoc } = await getFirestoreUtils()
    const user = getUserInfo()

    const logRef = doc(collection(firestore, `sharedGroups/${groupId}/activity`))
    await setDoc(logRef, {
      userId: user.uid,
      userName: user.displayName,
      userPhotoURL: user.photoURL || '',
      action,
      targetId,
      targetTitle,
      createdAt: new Date().toISOString(),
    })
  } catch (err) {
    console.warn('활동 로그 기록 실패 (무시됨):', err)
  }
}
